import React from 'react';
import {
  HubConnection,
  HubConnectionState,
  HubConnectionBuilder
} from '@microsoft/signalr';
import { useParams } from 'react-router-dom';

import { GameContext } from './game';
import { PlayerContext } from './player';
import { GameDocumentContext } from './game-document';
import { GetPlayerChatHub } from '../services/players';
import { ChatState } from '../types/state/websocket/chat-state';
import { NotificationContent, NotificationContext } from './notification';
import { sendPushNotification } from '../utils/game-engine/send-push-notification';

export interface PlayerChatContextProps {
  chatHub: HubConnection | null;
  newChat: ChatState[] | [];
  setNewChat: React.Dispatch<React.SetStateAction<ChatState[] | []>>;
}

const PlayerChatContext = React.createContext<PlayerChatContextProps | null>(
  null
);

const PlayerChatProvider = ({ children }: { children: React.ReactNode }) => {
  const { gameCode } = useParams();
  const [game] = React.useContext(GameContext);
  const [playerState] = React.useContext(PlayerContext);
  const [gameDocument] = React.useContext(GameDocumentContext);
  const [notification, setNotification] = React.useContext(NotificationContext);

  const [newChat, setNewChat] = React.useState<ChatState[] | []>([]);
  const [signalRHub, setSignalRHub] = React.useState<HubConnection | null>(
    null
  );

  const playerCode = playerState.playerState?.code;
  const players = React.useRef(game.gameState?.players ?? []);
  const teams = React.useRef(game.gameState?.teams ?? []);

  const isInLobby = ['waiting', 'ready'].includes(
    playerState.playerState?.status ?? ''
  );
  const preGameSetting = gameDocument.gameDocument?.settings.preGame;
  const inGameSetting =
    game.gameState?.runtime?.inGame ??
    gameDocument.gameDocument?.settings.inGame;

  const showAllPlayerChat = isInLobby
    ? preGameSetting?.showPreGameAllPlayerChat
    : inGameSetting?.showInGameAllPlayerChat;
  const showFacilitatorChat = isInLobby
    ? preGameSetting?.showPreGameFacilitatorChat
    : inGameSetting?.showInGameFacilitatorChat;
  const showDirectChat = isInLobby
    ? preGameSetting?.showPreGameDirectChat
    : inGameSetting?.showInGameDirectChat;
  const showTeamChat = isInLobby
    ? preGameSetting?.showPreGameTeamChat
    : inGameSetting?.showInGameTeamChat;
  const showGroupChat = isInLobby
    ? preGameSetting?.showPreGameGroupChat
    : inGameSetting?.showInGameGroupChat;

  React.useEffect(() => {
    players.current = game.gameState?.players ?? [];
  }, [game.gameState?.players]);

  React.useEffect(() => {
    teams.current = game.gameState?.teams ?? [];
  }, [game.gameState?.teams]);

  const sendNotification = ({
    senderCode,
    message,
    avatar,
    playerName
  }: {
    senderCode: string;
    message: string;
    avatar: string;
    playerName: string;
  }) => {
    if (playerCode && gameCode && playerCode !== senderCode) {
      sendPushNotification({
        message,
        avatar: avatar || '/favicon.ico',
        playerName,
        url: `${window.location.origin}/games/${gameCode}`
      });
    }
  };

  const showNewMessage = (playerName: string, message: string) => {
    const addScoreNotification: NotificationContent = {
      icon: 'chat',
      isHide: false,
      message: (
        <div className="d-flex flex-column">
          <strong>{playerName}</strong>
          <small>{message}</small>
        </div>
      )
    };
    const content: NotificationContent[] = notification.content;
    content.push(addScoreNotification);
    setNotification({ ...notification, content });
  };

  const onChatReceived = React.useCallback(
    (
      data: any,
      group:
        | 'global-chat'
        | 'player-chat'
        | 'team-chat'
        | 'group-chat'
        | 'facilitator-chat'
    ) => {
      if (group === 'global-chat' && !showAllPlayerChat) return;
      if (group === 'facilitator-chat' && !showFacilitatorChat) return;
      if (group === 'player-chat' && !showDirectChat) return;
      if (group === 'team-chat' && !showTeamChat) return;
      if (group === 'group-chat' && !showGroupChat) return;

      const { senderCode, senderName, senderAvatar, message, sentDateUtc } =
        data;

      const chat = {
        group,
        author: {
          id: senderCode,
          name: senderName,
          avatarUrl: senderAvatar
        },
        timestamp: new Date(sentDateUtc),
        text: message
      };
      setNewChat((prev) => [...prev, chat]);

      sendNotification({
        senderCode: data.senderCode,
        message: data.message,
        avatar: data.senderAvatar || '/favicon.ico',
        playerName: data.senderName
      });

      if (senderCode !== playerCode) showNewMessage(senderName, message);
    },
    [
      teams,
      players,
      gameCode,
      playerCode,
      showTeamChat,
      showGroupChat,
      showDirectChat,
      showAllPlayerChat,
      showFacilitatorChat
    ]
  );

  const onChatReceivedRef = React.useRef(onChatReceived);

  React.useLayoutEffect(() => {
    onChatReceivedRef.current = onChatReceived;
  }, [onChatReceived]);

  React.useEffect(() => {
    if (!playerCode || !gameCode || signalRHub) return;
    let isCanceled = false;

    GetPlayerChatHub(playerCode, gameCode).then((response) => {
      const { accessToken, endpointUrl } = response;

      if (accessToken && endpointUrl) {
        const hubConnectionSetup = new HubConnectionBuilder();
        hubConnectionSetup.withUrl(endpointUrl, {
          accessTokenFactory: () => accessToken
        });
        hubConnectionSetup.withAutomaticReconnect();

        const hubConnection = hubConnectionSetup.build();
        hubConnection
          .start()
          .then(() => {
            if (isCanceled) return hubConnection.stop();

            hubConnection.on('GlobalChatReceived', (data) =>
              onChatReceivedRef.current(data, 'global-chat')
            );
            hubConnection.on('GroupChatReceived', (data) =>
              onChatReceivedRef.current(data, 'group-chat')
            );
            hubConnection.on('TeamChatReceived', (data) =>
              onChatReceivedRef.current(data, 'team-chat')
            );
            hubConnection.on('PrivateChatReceived', (data) =>
              onChatReceivedRef.current(data, 'player-chat')
            );
            hubConnection.on('FacilitatorChatReceived', (data) =>
              onChatReceivedRef.current(data, 'facilitator-chat')
            );

            setSignalRHub(hubConnection);
          })
          .catch(() => {
            if (isCanceled) return;
          });

        return () => {
          isCanceled = true;

          if (hubConnection.state === HubConnectionState.Connected)
            hubConnection.stop();

          setSignalRHub(null);
        };
      }
    });
  }, [gameCode, playerCode]);

  return (
    <PlayerChatContext.Provider
      value={{
        newChat,
        chatHub: signalRHub,
        setNewChat
      }}>
      {children}
    </PlayerChatContext.Provider>
  );
};

export { PlayerChatContext, PlayerChatProvider };
